import type { ContentType } from '@/@types/common'

export interface JikePostURLScheme {
	type: 'ORIGINAL_POST'
	id: string
	commentId?: string
}

export interface JikeRepostURLScheme {
	type: 'REPOST'
	id: string
}

export interface JikeCommentURLScheme {
	type: 'COMMENT'
	id: string
	targetId: string
	targetType: ContentType
}

export interface JikeStoryURLScheme {
	type: 'STORY'
	id: string
}

export interface JikeUserURLScheme {
	type: 'USER'
	username: string
}

export interface JikeTopicURLScheme {
	type: 'TOPIC'
	id: string
}

export type JikeURLScheme =
	| JikePostURLScheme
	| JikeRepostURLScheme
	| JikeCommentURLScheme
	| JikeStoryURLScheme
	| JikeUserURLScheme
	| JikeTopicURLScheme

/**
 * 解析 Jike URL Scheme 中的参数
 * @example
 *
 * parseURLScheme('jike://page.jk/originalPost/6675110236ca95d8a2905b2b?locateCommentId=667a908679b610e1fdf1123e')
 * -> { type: 'ORIGINAL_POST', id: '6675110236ca95d8a2905b2b', commentId: '667a908679b610e1fdf1123e' }
 *
 * parseURLScheme('jike://page.jk/repost/6675110236ca95d8a2905b2b')
 * -> { type: 'REPOST', id: '6675110236ca95d8a2905b2b' }
 *
 * parseURLScheme('jike://page.jk/comment/6675110236ca95d8a2905b2b?targetId=6675110236ca95d8a2905b2b&targetType=ORIGINAL_POST')
 * -> { type: 'COMMENT', id: '6675110236ca95d8a2905b2b', targetId: '6675110236ca95d8a2905b2b', targetType: 'ORIGINAL_POST' }
 *
 * parseURLScheme('jike://page.jk/story/6675110236ca95d8a2905b2b')
 * -> { type: 'STORY', id: '6675110236ca95d8a2905b2b' }
 *
 * parseURLScheme('jike://page.jk/user/f2597557-293f-43ad-877f-64cf8edee389')
 * -> { type: 'USER', username: 'f2597557-293f-43ad-877f-64cf8edee389' }
 * @param url
 */
export default function parseJikeURLScheme(url: string): JikeURLScheme {
	const regex =
		/^jike:\/\/page\.jk\/(originalPost|repost|comment|story|user|topic)\/([a-zA-Z0-9]+)(?:\?(.+))?$/
	const match = url.match(regex)

	if (!match) {
		throw new Error('Invalid Jike URL Scheme')
	}

	const [, type, id, query] = match

	switch (type) {
		case 'originalPost': {
			const [, commentId] = query?.match(/locateCommentId=([a-zA-Z0-9]+)/) || []
			return { type: 'ORIGINAL_POST', id, commentId }
		}
		case 'repost':
			return { type: 'REPOST', id }
		case 'comment': {
			// extract targetId and targetType from query
			const [, targetType, targetId] =
				query.match(/targetType=([a-zA-Z0-9_]+)&targetId=([a-zA-Z0-9]+)/) || []
			return { type: 'COMMENT', id, targetId, targetType: targetType as ContentType }
		}
		case 'story':
			return { type: 'STORY', id }
		case 'user':
			return { type: 'USER', username: id }
		case 'topic':
			return {
				type: 'TOPIC',
				id,
			}
		default:
			throw new Error('Invalid Jike URL Scheme')
	}
}

export function toJikeURLScheme(
	urlScheme: JikePostURLScheme | JikeRepostURLScheme | JikeCommentURLScheme
): string {
	switch (urlScheme.type) {
		case 'ORIGINAL_POST':
			return `jike://page.jk/originalPost/${urlScheme.id}`
		case 'REPOST':
			return `jike://page.jk/repost/${urlScheme.id}`
		case 'COMMENT':
			return `jike://page.jk/comment/${urlScheme.id}?targetId=${urlScheme.targetId}&targetType=${urlScheme.targetType}`
		default:
			throw new Error('Invalid Jike URL Scheme')
	}
}
